import React from 'react';
import { ENTITY_TYPES } from '@adsk/bim360-matrix-react-entity-linker';
import handleFetchErrors from '@adsk/bim360-matrix-react-helpers/es/handleFetchErrors';
import { COST_ENV_CONFIG_KEYS, COST_STATUS_MAPPING } from './consts';
import getEnvConfig from './helpers/getEnvConfig';
import formatPcoName from './helpers/formatPCOName';
import PcoCreateForm from './PcoCreateForm';
import PcoEntityBlock from './PcoEntityBlock';
import PcoEntityPickerItem from './PcoEntityPickerItem';
import PcoLinkedEntitiesCellBlock from './PcoLinkedEntitiesCellBlock';
import PcoLinkedEntitiesCellItem from './PcoLinkedEntitiesCellItem';
import getDeeplinkUrl from './helpers/getDeeplinkUrl';

export default {
  entityType: ENTITY_TYPES.PCO,
  metadata: {
    containerType: 'cost'
  },
  createPlugin: ({ env, region, projectId, containerId, getToken }) => {
    const baseUrl = getEnvConfig(env, region, COST_ENV_CONFIG_KEYS.COST_BE);
    const parseEntity = entity => ({
      ...entity,
      entityType: ENTITY_TYPES.PCO,
      status: COST_STATUS_MAPPING[entity.costStatus]
    });
    const EntityBlock = props => (
      <PcoEntityBlock {...props} env={env} region={region} projectId={projectId} />
    );

    const LinkedEntitiesCellBlock = props => (
      <PcoLinkedEntitiesCellBlock {...props} env={env} region={region} projectId={projectId} />
    );

    const createEntity = entity =>
      getToken()
        .then(token =>
          fetch(`${baseUrl}/v1/containers/${containerId}/change-orders/pco`, {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            method: 'POST',
            body: JSON.stringify(entity)
          })
        )
        .then(handleFetchErrors)
        .then(res => res.json())
        .then(parseEntity)
        .then(entity => ({
          ...entity,
          deeplinkUrl: getDeeplinkUrl(env, region, projectId, entity.id)
        }));

    const CreateNewEntityForm = props => (
      <PcoCreateForm {...props} getToken={getToken} onCreate={createEntity} />
    );

    let isContainerInitialized = false;
    const get = (url, token) =>
      fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })
        .then(handleFetchErrors)
        .then(res => res.json());
    const apiCache = { [containerId]: null };

    return {
      queryEntities: query => {
        const pageLimit = query.pageLimit;
        const searchExpression = query.searchExpression;
        const offset = query.offset || 0;
        const sort = query.sort || 'number+desc';
        const searchParams = new URLSearchParams();

        if (pageLimit) {
          searchParams.set('limit', pageLimit);
        }
        if (offset) {
          searchParams.set('offset', offset);
        }
        if (searchExpression) {
          const filter = JSON.stringify({
            valid: true,
            column: 'name',
            type: 'string',
            value: searchExpression,
            operator: 'contains'
          });
          searchParams.set('q', `[${filter}]`);
        }

        const searchParamsStr = searchParams.toString();
        const queryString = searchParamsStr ? `?${searchParamsStr}&sort=${sort}` : `?sort=${sort}`;
        return getToken()
          .then(token =>
            isContainerInitialized
              ? token
              : new Promise((resolve, reject) => {
                // make sure cost container is initialized, it costs some time when cost module is initializing.
                let count = 0;
                const containerStatusPulling = () =>
                  get(`${baseUrl}/v1/containers/${containerId}/status`, token)
                    .then(containerInitialized => {
                      count++;
                      if (containerInitialized) {
                        isContainerInitialized = true;
                        resolve(token);
                      } else if (count >= 10) {
                        throw Error('Container is initializing...');
                      } else {
                        setTimeout(containerStatusPulling, 6000);
                      }
                    })
                    .catch(e => reject(e));

                containerStatusPulling();
              })
          )
          .then(token =>
            get(`${baseUrl}/v1/containers/${containerId}/change-orders/pco${queryString}`, token)
          )
          .then(response => {
            const nextOffset = pageLimit ? offset + pageLimit : undefined;
            const nextPageQuery =
              nextOffset && nextOffset < response.pagination.totalResults
                ? { ...query, offset: nextOffset || offset }
                : undefined;
            return {
              data: response.results.map(parseEntity),
              nextPageQuery
            };
          });
      },
      getEntitiesByIds: entityIds =>
        getToken()
          .then(token =>
            get(
              `${baseUrl}/v1/containers/${containerId}/change-orders/pco?id=${entityIds.join(',')}`,
              token
            )
          )
          .then(response => ({ data: response.results.map(parseEntity) })),
      createEntity,
      canCreate: () => {
        if (apiCache[containerId] !== null) {
          return Promise.resolve(apiCache[containerId]);
        }
        return getToken()
          .then(token => get(`${baseUrl}/v1/containers/${containerId}/permissions/current`, token))
          .then(
            response =>
              Array.isArray(response) &&
              response.some(
                ({ resourceType, permissionLevel }) =>
                  resourceType === 'CostItem' && permissionLevel === 'fullControl'
              )
          )
          .then(value => {
            apiCache[containerId] = value;
            return value;
          })
          .catch(() => false);
      },
      EntityBlock,
      LinkedEntitiesCellBlock,
      EntityPickerItem: PcoEntityPickerItem,
      formatEntityDisplayText: formatPcoName,
      CreateNewEntityForm,
      LinkedEntitiesCellItem: PcoLinkedEntitiesCellItem,
      clearCache: () => {
        apiCache[containerId] = null;
      }
    };
  }
};
